import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import PasswordIcon from "@material-ui/icons/LockOutlined";
import Box from "@material-ui/core/Box";
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import makeStyles from "@material-ui/core/styles/makeStyles";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import LoadingButton from "../../../components/LoadingButton";
import IconTextField from "../../Mobile/Components/IconTextField";
import { callAPI } from "../../../lib/common";
import Button from "@material-ui/core/Button";
import BoxActions from "./BoxActions";
import BoxHeading from "./BoxHeading";
import BoxContainer from "./BoxContainer";
import { useAuth } from "../../providers/AuthProvider";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import FormikTextField from "../../../components/Common/FormikTextField";
import Level2BoxContainer from "./Level2BoxContainer";
import Level2BoxHeading from "./Level2BoxHeading";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
  },
  form: {
    flexGrow: 1,
    marginBottom: 0,
  },
}));

export default ({ email, handleGoBack, handleLoginSuccess }) => {
  const { t } = useTranslation();
  const {
    isAuthenticating,
    isAuthenticated,
    loginError,
    user,
    login: authLogin,
  } = useAuth();
  const classes = useStyles();
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState();
  const isMobile = useIsMobileDevice();

  useEffect(() => {
    if (isAuthenticated) {
      if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
        window.epmsAnalytics.onLogin(user);
      }

      handleLoginSuccess();
    }
  }, [isAuthenticated]);

  const openSendForgotPasswordDialog = (e) => {
    e.preventDefault();
    setForgotPasswordDialogOpen(true);
  };

  console.log("Login with email", email);

  return (
    <Level2BoxContainer>
      <Level2BoxHeading>
        {t("Welcome back, %s", { postProcess: "sprintf", sprintf: [email] })}
      </Level2BoxHeading>

      <Box mb={1} sx={{ textAlign: "center" }}>
        <Trans>Please enter your password</Trans>
      </Box>

      <Formik
        initialValues={{
          password: "",
        }}
        validationSchema={loginSchema(t)}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          authLogin(email, values.password);
        }}
      >
        {({ submitForm, isValid, errors }) => {
          return (
            <Form className={classes.form}>
              <Field
                id="new-subject-code-password"
                component={FormikTextField}
                type="password"
                label={t("Password")}
                name="password"
                variant="outlined"
                fullWidth
                autoFocus
              />

              <Box
                className={classes.subText}
                mt={1}
                mb={4}
                display="flex"
                justifyContent="flex-end"
              >
                <Link href="#" onClick={openSendForgotPasswordDialog}>
                  <Trans>Forgot your password?</Trans>
                </Link>{" "}
              </Box>

              <p>
                <FormHelperText error={!!loginError}>
                  {loginError}
                </FormHelperText>
              </p>

              <BoxActions>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGoBack}
                  size={isMobile ? "medium" : "large"}
                >
                  <Trans>Go back</Trans>
                </Button>
                <LoadingButton
                  variant="primary"
                  isLoading={isAuthenticating}
                  onClick={submitForm}
                  disabled={!isValid}
                  fullWidth
                  size={isMobile ? "medium" : "large"}
                >
                  <Trans>Log in</Trans>
                </LoadingButton>
              </BoxActions>
              {forgotPasswordDialogOpen && (
                <ForgotPasswordDialog
                  email={email}
                  onClose={() => setForgotPasswordDialogOpen(false)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </Level2BoxContainer>
  );
};

const ForgotPasswordDialog = ({ email, onClose }) => {
  const [sending, setSending] = useState(true);
  const [error, setError] = useState(null);
  const [completeMessage, setCompleteMessage] = useState("");

  useEffect(() => {
    callAPI(
      `/sections/prophoto/ajax/send_password_reminder.php?json=1&email=${email}`,
    )
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          setCompleteMessage(res.message);
        }
      })
      .catch((err) => {
        setError(t("Error sending password reset instructions."));
      })
      .finally(() => {
        setSending(false);
      });
  }, []);

  if (sending) {
  }
  const getContent = () => {
    if (sending) {
      return (
        <DialogContent>
          <Trans>Sending...</Trans>
        </DialogContent>
      );
    }
    if (error || completeMessage) {
      return (
        <>
          <DialogContent>{error || completeMessage}</DialogContent>
          <DialogActions>
            <Button color="primary" onClick={onClose}>
              <Trans>Ok</Trans>
            </Button>
          </DialogActions>
        </>
      );
    }
  };

  return <Dialog open={true}>{getContent()}</Dialog>;
};

const loginSchema = (t) => () => {
  return Yup.object().shape({
    password: Yup.string()
      .min(3, t("That's too short!"))
      .required(t("This is required!")),
  });
};
